import { NavLink, useLocation, useSearchParams } from 'react-router-dom'
import { VIEWS, destinationForPath } from '../navViews'
import { ALL_DESTINATIONS, canReach } from '../nav'
import { navSubjectFrom } from '../navSubject'
import { useSession } from '../useSession'
import { useViewCounts } from './useNavBadges'

/**
 * The views inside whichever destination you are in (D-322, Rule 3).
 *
 * **Rendered by the shell, not by each screen.** Seven screens needed one and
 * none of them knew it — they were reachable from a header menu that no longer
 * exists. Putting the row in one place means a screen cannot be filed under a
 * destination and then forget to say so, and it cannot grow a second navigation
 * of its own either, which is the fault this whole rework started from.
 *
 * **Nothing renders for a destination with no views**, which is most of them.
 * Rule 3 says the row is earned: a single-screen destination that draws an empty
 * bar is chrome pretending to be navigation.
 */
export default function ViewBar() {
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const { session } = useSession()
  const counts = useViewCounts()

  // A kiosk reaches nothing, and the shell already refuses it (D-233).
  if (!session || session.isKiosk) return null

  const key = destinationForPath(pathname)
  if (!key) return null

  const subject = navSubjectFrom(session)

  /*
   * **The destination first, then its views** (D-393).
   *
   * This filtered views by their own capabilities and never asked whether the
   * reader may be on the destination at all — so `observed`, Tonya at
   * `/my-classes` got a bar offering *Classes · Attendance · Absences* above an
   * `<h1>Not your page</h1>`. Three links on a screen that had just refused
   * her.
   *
   * `RequireDestination` renders the refusal from exactly this function, and
   * its own docstring says it exists to stop a screen being offered to somebody
   * it turns away. The bar sat outside the guard and answered a narrower
   * question, which is the *"one function answers a question two screens ask"*
   * shape — and the disagreement is invisible, because both are individually
   * right.
   *
   * It bites whoever holds a view's capability without the destination's:
   * Tonya has `TakeAttendance` like every admin and owns no classes, and a
   * substitute reaches `/registers` the same way.
   */
  if (!canReach(subject, key)) return null

  const views = (VIEWS[key] ?? []).filter((view) => {
    // A choice needs both sides to exist — see `NavView.onlyWhenBoth`.
    if (view.onlyWhenBoth === true && !(subject.teaches && subject.isParent)) return false

    return !view.needsAny?.length || view.needsAny.some((c) => subject.capabilities.includes(c))
  })

  /*
   * **A row of one is hidden only when it is a duplicate of the door you came
   * through**, and the first cut of this rule got that backwards.
   *
   * D-257 is about a control that changes nothing: a lone view whose address
   * is the destination you are already on. But somebody granted `ManageStaff`
   * alone lands on People's own address, Families, and her one surviving view
   * is *Staff & access* — a different screen, and the only way to it. Hiding
   * that row leaves her with a rail item that opens the wrong page and no
   * route to the right one, which is precisely the case `nav.ts` opens People
   * on `ManageStaff` in order to serve (D-5).
   */
  if (views.length === 0) return null

  const home = ALL_DESTINATIONS.find((d) => d.key === key)?.to
  if (views.length === 1 && views[0].to === home) return null

  /*
   * What travels with a `sharesAnchor` view (D-360).
   *
   * **An allow-list rather than the whole search**, because most of what can be
   * in the address belongs to exactly one of the two halves — `?dancer=` above
   * all. Carrying everything would move a child id onto the teaching week,
   * where it means nothing and where the redirect predicate would read it as a
   * request for the other half and bounce her straight back.
   */
  const anchor = new URLSearchParams()
  for (const name of ['week', 'view']) {
    const value = params.get(name)
    if (value) anchor.set(name, value)
  }
  const carried = anchor.toString()

  return (
    <div className="border-b border-line bg-surface">
      <nav aria-label="Views" className="page-shell flex gap-1 overflow-x-auto px-5 py-2">
        {views.map((view) => (
          <NavLink
            key={view.to}
            to={view.sharesAnchor && carried ? `${view.to}?${carried}` : view.to}
            end={view.end}
            className={({ isActive }) =>
              [
                'shrink-0 rounded-lg px-3 py-1.5 text-[13px] font-semibold no-underline transition-colors',
                /*
                 * The same accent the rail uses, because it is the same claim.
                 *
                 * This said `bg-surface-2 text-ink` — grey — while the rail and
                 * the bottom bar both say *you are here* in the accent. Three
                 * navigations, one meaning, two signals; the odd one out was
                 * the row sitting closest to the content it describes (D-338).
                 */
                isActive ? 'bg-accent-soft text-accent-2' : 'text-ink-3 hover:text-ink',
              ].join(' ')
            }
          >
            {view.label}

            {/* On the item, never summed onto the row (D-167, D-324). */}
            {(counts[view.to] ?? 0) > 0 && (
              <span className="ml-1.5 text-[12px] font-bold text-accent tabular-nums">
                {counts[view.to]}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
